import {
  OUTBOUND_CONCURRENCY_MAX,
  OUTBOUND_CONCURRENCY_MIN,
} from "@utils/clinicFields";

const INPUT_ID = "outbound-max-concurrent-calls";
const TRANSFER_ID = "transfer-campaigns-enabled";

/**
 * Admin-only outbound settings for one clinic.
 *
 * Only ever mounted inside EditClinicDrawer under /admin/clinics. The server
 * validates the range authoritatively; the hint here is advice, not a gate,
 * so an admin is never blocked from saving by the UI alone.
 *
 * `transferEnabled` is whether the clinic may start transfer-mode campaigns
 * (calls handed to the clinic's own line instead of booked through a PMS).
 * The server refuses the mode for a clinic without it.
 */
export default function OutboundEditor({
  enabled, maxConcurrent, transferEnabled = false, onChangeField,
}) {
  const n = Number(maxConcurrent);
  const outOfRange =
    maxConcurrent !== "" &&
    maxConcurrent !== null &&
    maxConcurrent !== undefined &&
    (!Number.isFinite(n) || n < OUTBOUND_CONCURRENCY_MIN || n > OUTBOUND_CONCURRENCY_MAX);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
      <div>
        <div className="text-sm font-semibold">Outbound calls</div>
        <div className="text-xs text-gray-600 mt-1">
          How this clinic's AI places reactivation campaign calls.
        </div>
      </div>

      <div>
        <label htmlFor={INPUT_ID} className="block text-sm font-medium mb-1">
          Max concurrent outbound calls
        </label>
        <input
          id={INPUT_ID}
          type="number"
          inputMode="numeric"
          min={OUTBOUND_CONCURRENCY_MIN}
          max={OUTBOUND_CONCURRENCY_MAX}
          step={1}
          value={maxConcurrent ?? ""}
          disabled={!enabled}
          onChange={(e) => {
            const raw = e.target.value;
            onChangeField(
              "outbound_max_concurrent_calls",
              raw === "" ? "" : parseInt(raw, 10),
            );
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100 disabled:text-gray-500"
        />
        <div className="text-xs text-gray-500 mt-1">
          Caps how many reactivation campaign calls this clinic's AI places at
          the same time. 1 is the safe default; raise it only once the clinic's
          phone line and front desk can absorb the extra bookings.
        </div>
        {outOfRange && (
          <div className="text-xs text-amber-700 mt-1">
            Must be between {OUTBOUND_CONCURRENCY_MIN} and {OUTBOUND_CONCURRENCY_MAX}.
            The server will reject anything outside that range.
          </div>
        )}
        {!enabled && (
          <div className="text-xs text-gray-500 mt-1">
            Enable outbound calls above to change this.
          </div>
        )}
      </div>

      <label htmlFor={TRANSFER_ID} className="flex items-start gap-3">
        <input
          id={TRANSFER_ID}
          type="checkbox"
          className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          checked={Boolean(transferEnabled)}
          onChange={(e) => onChangeField("transfer_campaigns_enabled", e.target.checked)}
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">
            Enable transfer campaigns
          </span>
          <span className="block text-xs text-gray-500">
            Lets the clinic start campaigns that hand an interested patient to the
            clinic's own line instead of booking through a scheduling system. Needs
            the clinic phone above; the handover is only as good as whoever answers it.
          </span>
        </span>
      </label>
    </div>
  );
}
